export const TERMINAL_BACKTEST_STATES = new Set(['completed', 'failed', 'cancelled']);

export const isPremiumPlan = (billing) => (
  typeof billing?.entitlements?.premium_backtesting === 'boolean'
    ? billing.entitlements.premium_backtesting
    : billing?.plan === 'premium'
);

export const dateOnly = (value) => (value ? String(value).slice(0, 10) : '');

// The server returns compatible snapshots in its immutable authority order:
// newest finalized_at first, then snapshot id. Keep the UI default tied to that
// order instead of inventing a client-side ranking.
export const backtestSnapshotKey = (snapshot) => (
  snapshot ? `${snapshot.snapshot_id}:${snapshot.series_id}` : ''
);

export const selectBacktestSnapshot = ({ snapshots = [], selectedKey = '', manual = false }) => {
  const selected = snapshots.find((snapshot) => backtestSnapshotKey(snapshot) === selectedKey);
  if (manual && selected) return selected;
  return snapshots[0] || null;
};

export const eligibleBacktestRange = (snapshot) => ({
  start: dateOnly(snapshot?.earliest_eligible_date),
  end: dateOnly(snapshot?.latest_eligible_date),
});

export const snapshotUnavailabilityMessage = (unavailability, fallbackSymbol = '') => {
  const symbol = String(unavailability?.symbol || fallbackSymbol || '').trim().toUpperCase();
  const subject = symbol || 'this stock';
  const requiredLookback = Number(unavailability?.required_lookback);
  const requiredTimeframe = String(unavailability?.required_timeframe || '').trim();
  const messages = {
    adjustment_basis_unavailable: unavailability?.required_adjustment_basis === 'split_adjusted_ohlcv_v1'
      ? `Pinngro does not currently have the split-adjusted historical data required to backtest this short strategy for ${subject}.`
      : `Pinngro does not currently have historical data with the required adjustment to backtest this strategy for ${subject}.`,
    insufficient_history: Number.isFinite(requiredLookback)
      ? `Pinngro has historical data for ${subject}, but not enough completed sessions to run this strategy yet. This strategy needs ${requiredLookback} sessions of history before the simulation can begin.`
      : `Pinngro has historical data for ${subject}, but not enough completed sessions to run this strategy yet.`,
    no_finalized_snapshot: `Pinngro does not currently have a finalized historical dataset available for ${subject}.`,
    timeframe_unavailable: requiredTimeframe
      ? `Pinngro does not currently have the ${requiredTimeframe} historical data required to backtest this strategy for ${subject}.`
      : `Pinngro does not currently have the historical timeframe required to backtest this strategy for ${subject}.`,
    snapshot_contract_unavailable: `Pinngro does not currently have a compatible finalized historical dataset available for ${subject}.`,
  };
  return messages[unavailability?.reason]
    || `Pinngro could not verify historical dataset availability for ${subject}. Please try again.`;
};

export const isBacktestRangeOutsideEligible = ({ snapshot, startDate, endDate }) => {
  const eligibleRange = eligibleBacktestRange(snapshot);
  if (!snapshot || !eligibleRange.start || !eligibleRange.end || !startDate || !endDate) return false;
  return startDate > endDate
    || startDate < eligibleRange.start
    || endDate > eligibleRange.end;
};

export const backtestErrorMessage = (error) => {
  if (error?.response?.status === 403) {
    return 'Backtesting requires an active Premium subscription.';
  }
  if (error?.response?.status === 404) {
    return 'That backtest is unavailable or does not belong to your account.';
  }
  const code = error?.response?.data?.detail?.code;
  const details = error?.response?.data?.detail?.details || {};
  if (code === 'insufficient_history' && details.earliest_eligible_date) {
    return `This strategy's first valid simulation date is ${details.earliest_eligible_date}. Choose that date or later.`;
  }
  const messages = {
    backtest_snapshot_invalid: 'The selected snapshot cannot cover this backtest range.',
    backtest_snapshot_unverified: 'The selected dataset has not passed integrity verification.',
    backtest_strategy_data_contract_mismatch: 'The selected strategy and dataset use different timeframes.',
    backtest_idempotency_conflict: 'This request identifier was already used with different inputs.',
    backtest_result_not_found: 'The completed result is not available.',
    backtest_evaluator_unsupported: 'This strategy version is not available for verification.',
    backtest_sentiment_evidence_unavailable: 'This strategy needs historical sentiment evidence that Pinngro does not currently retain for Backtesting.',
  };
  return messages[code] || 'The backtest request could not be completed. Please try again.';
};

export const validateBacktestDraft = ({ botId, bot, snapshot, startDate, endDate, assumptions }) => {
  const errors = {};
  const eligibleRange = eligibleBacktestRange(snapshot);
  if (!botId) errors.botId = 'Choose one of your bots.';
  if (!snapshot) errors.snapshot = 'Choose a finalized market-data snapshot.';
  if (bot && snapshot && bot.required_timeframe !== snapshot.timeframe) {
    errors.snapshot = 'The selected strategy and dataset use different timeframes.';
  } else if (
    bot
    && snapshot
    && Number(snapshot.selected_row_count) < Number(bot.required_lookback)
  ) {
    errors.snapshot = `This strategy needs at least ${bot.required_lookback} historical rows.`;
  }
  if (snapshot && (!eligibleRange.start || !eligibleRange.end)) {
    errors.range = 'Pinngro could not verify valid simulation dates for this dataset. Choose the dataset again or try another one.';
  } else if (!startDate || !endDate) {
    errors.range = 'Choose a start and end date.';
  } else if (startDate > endDate) {
    errors.range = 'The start date must be on or before the end date.';
  } else if (snapshot) {
    if (startDate < eligibleRange.start) {
      errors.range = `This strategy's first valid simulation date is ${eligibleRange.start}. Choose that date or later.`;
    } else if (endDate > eligibleRange.end) {
      errors.range = `Choose an end date on or before ${eligibleRange.end}.`;
    }
  }
  const initialCapital = Number(assumptions.initial_capital);
  const allocation = Number(assumptions.allocation_percent);
  const commission = Number(assumptions.commission_per_share);
  const slippage = Number(assumptions.slippage_bps);
  if (!Number.isFinite(initialCapital) || initialCapital <= 0) {
    errors.initialCapital = 'Initial capital must be greater than zero.';
  }
  if (!Number.isFinite(allocation) || allocation <= 0 || allocation > 100) {
    errors.allocation = 'Allocation must be between 0 and 100 percent.';
  }
  if (!Number.isFinite(commission) || commission < 0) {
    errors.commission = 'Commission cannot be negative.';
  }
  if (!Number.isFinite(slippage) || slippage < 0) {
    errors.slippage = 'Slippage cannot be negative.';
  }
  return errors;
};

export const buildBacktestRequest = ({ botId, snapshot, startDate, endDate, assumptions, idempotencyKey }) => {
  const profileAssumptions = Object.fromEntries(
    Object.entries(assumptions).filter(([key]) => key !== 'assumptions_hash'),
  );
  return {
    bot_id: Number(botId),
    snapshot_id: snapshot.snapshot_id,
    symbol: snapshot.symbol,
    start_date: startDate,
    end_date: endDate,
    provider: snapshot.provider,
    feed: snapshot.feed,
    timeframe: snapshot.timeframe,
    adjustment_basis: snapshot.adjustment_basis,
    idempotency_key: idempotencyKey,
    execution_assumptions: {
      trailing_stop_policy: 'disabled',
      maximum_positions: 1,
      cash_policy: 'cash_only_no_margin',
      position_mode: 'long_only',
      ...profileAssumptions,
      initial_capital: String(assumptions.initial_capital),
      allocation_percent: String(assumptions.allocation_percent),
      commission_per_share: String(assumptions.commission_per_share),
      slippage_bps: String(assumptions.slippage_bps),
      partial_fills: false,
      stochastic_seed: null,
    },
  };
};

export const chartPoints = (values, accessor, width = 720, height = 220) => {
  if (!values?.length) return '';
  const numbers = values.map(accessor).map(Number).filter(Number.isFinite);
  if (!numbers.length) return '';
  const minimum = Math.min(...numbers);
  const maximum = Math.max(...numbers);
  const spread = maximum - minimum || 1;
  const denominator = Math.max(numbers.length - 1, 1);
  return numbers.map((value, index) => {
    const x = (index / denominator) * width;
    const y = height - ((value - minimum) / spread) * height;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');
};

export const summarizeDecisions = (decisions = []) => decisions.reduce((summary, row) => {
  const key = row.decision || 'unknown';
  return { ...summary, [key]: (summary[key] || 0) + 1 };
}, {});

const shortTradeContractIsValid = (trade) => (
  trade?.position_direction === 'SHORT'
  && trade?.entry_action === 'enter_short'
  && trade?.entry_side === 'SELL'
  && trade?.entry_effect === 'OPEN'
  && trade?.exit_action === 'exit_short'
  && trade?.exit_side === 'BUY'
  && trade?.exit_effect === 'CLOSE'
);

export const presentBacktestResult = (result = {}) => {
  const nestedDirection = result?.result_payload?.position_direction;
  const positionDirection = result.position_direction || nestedDirection || 'LONG';
  const isShort = positionDirection === 'SHORT';
  const trades = (result.trades || []).map((trade) => {
    if (!isShort) {
      return {
        ...trade,
        presentation_entry_label: 'Entry',
        presentation_exit_label: 'Exit',
        presentation_entry_price: trade.entry_price,
        presentation_exit_price: trade.exit_price,
      };
    }
    const validContract = shortTradeContractIsValid(trade);
    return {
      ...trade,
      presentation_contract_valid: validContract,
      presentation_entry_label: 'OPEN SHORT',
      presentation_exit_label: 'COVER SHORT',
      presentation_entry_price: validContract ? trade.entry_fill_price : null,
      presentation_exit_price: validContract ? trade.cover_fill_price : null,
    };
  });
  return {
    ...result,
    position_direction: positionDirection,
    is_short: isShort,
    strategy_return_label: result.strategy_return_label
      || (isShort ? 'Short strategy return' : 'Total return'),
    benchmark_label: result.benchmark_label
      || (isShort ? 'Market buy-and-hold return' : 'Benchmark return'),
    entry_label: isShort ? 'OPEN SHORT' : 'Entry',
    exit_label: isShort ? 'COVER SHORT' : 'Exit',
    open_position_mark_price: result.open_position?.final_mark_price
      ?? result.open_position?.mark_price,
    trades,
  };
};
